import { createSelector } from 'reselect'

import API from '../api/api'

import { selectActiveBuilding } from './buildingSelectorReducer'

export const SET_BUILDING_GUESTS = 'GUESTS/SET_BUILDING_GUESTS'
export const SET_GUESTS = 'GUESTS/SET_GUESTS'
export const BAN_GUEST = 'GUESTS/BAN_GUEST'
export const ADD_GUEST = 'GUESTS/ADD_GUEST'
export const SUSPEND_GUEST = 'GUESTS/SUSPEND_GUEST'
export const RESTORE_GUEST = 'GUESTS/RESTORE_GUEST'

const initialState = {
  guests: {},
  buildingGuests: {},
}

const removeDuplicatedGuests = (guests) => {
  const guestsIds = []
  return guests.filter((guest) => {
    if (guestsIds.includes(guest.id)) {
      return false
    }
    guestsIds.push(guest.id)
    return true
  })
}

const guestsReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_BUILDING_GUESTS:
      const existingBuildingGuests = state.buildingGuests[action.buildingId] || []
      const updatedBuildingGuests =
        action.page === 1
          ? action.guests
          : removeDuplicatedGuests([...existingBuildingGuests, ...action.guests])
      return {
        ...state,
        buildingGuests: {
          ...state.buildingGuests,
          [action.buildingId]: updatedBuildingGuests,
        },
      }
    case SET_GUESTS:
      return { ...state, guests: { ...state.guests, [action.clientId]: action.guests } }
    case BAN_GUEST:
      const copyGuests = state.guests[action.clientId] || []
      return {
        ...state,
        guests: {
          ...state.guests,
          [action.clientId]: copyGuests.map((guest) => {
            if (guest.id === action.guestId) {
              return { ...guest, banned: true, keyAccess: false }
            }
            return guest
          }),
        },
      }
    case ADD_GUEST:
      const updatedClientGuests = [...(state.guests[action.clientId] || []), action.guest]
      return {
        ...state,
        guests: {
          ...state.guests,
          [action.clientId]: updatedClientGuests,
        },
      }
    case SUSPEND_GUEST: {
      const copyGuests = state.guests[action.clientId] || []
      return {
        ...state,
        guests: {
          ...state.guests,
          [action.clientId]: copyGuests.map((guest) => {
            if (guest.id === action.guestId) {
              return { ...guest, isSaltoSpaceConnected: false }
            }
            return guest
          }),
        },
      }
    }
    case RESTORE_GUEST: {
      const copyGuests = state.guests[action.clientId] || []
      return {
        ...state,
        guests: {
          ...state.guests,
          [action.clientId]: copyGuests.map((guest) => {
            if (guest.id === action.guestId) {
              return { ...guest, isSaltoSpaceConnected: true }
            }
            return guest
          }),
        },
      }
    }
    default:
      return state
  }
}

// Actions
export const fetchBuildingGuests = (page = 1, buildingId = 0, search = '', pageSize = 50) => {
  const skip = (page - 1) * pageSize
  return async (dispatch) => {
    try {
      const res = await API.getBuildingGuests(buildingId, { search, skip, pageSize })
      dispatch({ type: SET_BUILDING_GUESTS, buildingId, guests: res.guests, page })
    } catch (e) {
      throw new Error(e)
    }
  }
}

export const fetchClientGuests = (clientId) => {
  return async (dispatch) => {
    try {
      const res = await API.getClientGuests(clientId)
      dispatch({ type: SET_GUESTS, clientId, guests: res })
    } catch (e) {
      throw new Error(e)
    }
  }
}

export const banGuest = (clientId, guestId) => {
  return async (dispatch) => {
    try {
      await API.banClientGuest(clientId, guestId)
      dispatch({ type: BAN_GUEST, clientId, guestId })
    } catch (e) {
      throw new Error(e)
    }
  }
}

export const checkInGuest = (clientId, guestId) => {
  return async () => {
    try {
      await API.checkInClientGuest(clientId, guestId)
    } catch (e) {
      throw new Error(e)
    }
  }
}

export const addGuest = (clientId, guest) => {
  return async (dispatch) => {
    try {
      await API.addNewVisitorGuest(clientId, guest)
      dispatch({ type: ADD_GUEST, clientId, guest })
    } catch (error) {
      throw new Error(error)
    }
  }
}

// Helpers
export const findGuestById = (guests, guestId) => guests.find(({ id }) => id === guestId)

// Selectors
export const selectGuests = (state) => state.guests.guests
export const selectBuildingGuests = (state) => {
  const buildingId = selectActiveBuilding(state).id
  return state.guests.buildingGuests[buildingId] || []
}

export const selectGuestsByClientId = createSelector(
  selectGuests,
  (_, clientId) => clientId,
  (guests, clientId) => guests[clientId] || [],
)

export default guestsReducer
